import React, { createContext, ReactNode, useContext, useEffect, useMemo, useState } from 'react';
import { generateMenuItems, routes } from '../infoTrac/components/Layout/Menu';

export interface MenuItem {
  path: string;
  label: string;
  icon?: React.ReactNode;
  hidden?: boolean;
  settingsVisible?: boolean;
  order?: number;
  children?: MenuItem[];
  visibleOnlyWithAccount?: boolean;
}

interface MenuProviderProps {
  menuItems: MenuItem[];
  hiddenMenuItems: MenuItem[];
  handleMenuItems: (payload: MenuItem[]) => void;
  handleHiddenMenuItems: (payload: MenuItem[]) => void;
  handleTriggerRegenerate: () => void;
}

const defaultValue: MenuProviderProps = {
  menuItems: [],
  hiddenMenuItems: [],
  handleMenuItems: () => undefined,
  handleHiddenMenuItems: () => undefined,
  handleTriggerRegenerate: () => undefined,
};

export const MenuContext = createContext<MenuProviderProps>(defaultValue);

function MenuProvider({ children }: { children: ReactNode }) {
  const [menuItems, setMenuItems] = useState<MenuItem[]>([]);
  const [hiddenMenuItems, setHiddenMenuItems] = useState<MenuItem[]>([]);
  const [triggerRegenerate, setTriggerRegenerate] = useState(0);

  const handleMenuItems = (newItems: MenuItem[]) => {
    setMenuItems(newItems);
  };

  const handleHiddenMenuItems = (newItems: MenuItem[]) => {
    setHiddenMenuItems(newItems);
  };

  /** This function needed to re-generate menu items after some actions. For example after hide routes in settings */
  const handleTriggerRegenerate = () => setTriggerRegenerate(Date.now);

  useEffect(() => {
    const items = generateMenuItems(routes).filter(item => item.settingsVisible); // Show only items which active in settings.

    setMenuItems(items);
  }, [routes, triggerRegenerate]);

  const memoizedValue: MenuProviderProps = useMemo(
    () => ({
      menuItems,
      hiddenMenuItems,
      handleMenuItems,
      handleHiddenMenuItems,
      handleTriggerRegenerate,
    }),
    [menuItems, hiddenMenuItems],
  );

  return <MenuContext.Provider value={memoizedValue}>{children}</MenuContext.Provider>;
}

export default MenuProvider;

export const useMenuProvider = (): MenuProviderProps => useContext(MenuContext);
